import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, Button, Input, message, Card, Tabs, Spin, Alert, Tag, Space, Divider, Typography } from 'antd';
import { BugOutlined, RobotOutlined, SyncOutlined, CheckCircleOutlined, CloseCircleOutlined, CopyOutlined, SettingOutlined, CodeOutlined, BarChartOutlined, ToolOutlined } from '@ant-design/icons';
import CodeEditor from './CodeEditor';
import AutoReviewStatsDisplay from './AutoReviewStatsDisplay';
import { judgeScenarioApi } from '../services/api';

const { TextArea } = Input;
const { TabPane } = Tabs;
const { Text } = Typography;

// 脚本类型配置
const SCRIPT_TYPES = [
  { 
    value: 'PRE_SCRIPT', 
    label: '前置脚本', 
    desc: '在服务调用前执行，准备请求参数',
    icon: <SettingOutlined />,
    category: 'common'
  },
  { 
    value: 'ASSERTION_SCRIPT', 
    label: '断言脚本', 
    desc: '验证服务响应与期望答案',
    icon: <CodeOutlined />,
    category: 'common'
  },
  { 
    value: 'METRIC_STATS_SCRIPT', 
    label: '指标统计脚本', 
    desc: '预设指标统计计算',
    icon: <BarChartOutlined />,
    category: 'common'
  },
];

/**
 * 脚本调试弹窗组件
 * 用于单独调试某一种脚本，支持出错后填写规则描述重新生成
 */
function ScriptDebugModal({ 
  visible, 
  onCancel, 
  scenario, 
  serviceConfig,
  scenarioCategory,
  getFormScripts,
  onApply 
}) {
  const [form] = Form.useForm();
  const [currentScriptType, setCurrentScriptType] = useState('ASSERTION_SCRIPT');
  const [scripts, setScripts] = useState({
    PRE_SCRIPT: '',
    ASSERTION_SCRIPT: '',
    METRIC_STATS_SCRIPT: '',
  });
  const [debugLoading, setDebugLoading] = useState(false);
  const [regenerateLoading, setRegenerateLoading] = useState(false);
  const [debugResult, setDebugResult] = useState(null);
  const [executionLogs, setExecutionLogs] = useState([]);
  const [debugRuleDesc, setDebugRuleDesc] = useState('');
  
  // 指标统计脚本输出预览相关状态
  const [metricOutputPreviewData, setMetricOutputPreviewData] = useState(null);
  const [showMetricOutputPreview, setShowMetricOutputPreview] = useState(false);

  // 弹窗打开时初始化
  useEffect(() => {
    if (visible) {
      // 从父组件表单获取脚本
      const formScripts = getFormScripts ? getFormScripts() : {};
      setScripts({
        PRE_SCRIPT: formScripts.preScript || '',
        ASSERTION_SCRIPT: formScripts.assertionScript || '',
        METRIC_STATS_SCRIPT: formScripts.metricStatsScript || '',
      });
      setDebugResult(null);
      setExecutionLogs([]);
      setDebugRuleDesc('');
    }
  }, [visible, getFormScripts]);

  // 获取当前脚本类型配置
  const getCurrentTypeConfig = () => {
    return SCRIPT_TYPES.find(t => t.value === currentScriptType) || SCRIPT_TYPES[1];
  };

  // 获取当前场景可用的脚本类型
  const getAvailableScriptTypes = () => {
    return SCRIPT_TYPES.filter(item => {
      // 如果没有指定场景类别限制，或者当前场景匹配，则显示
      if (!item.scenarioCategory) return true;
      return item.scenarioCategory === scenarioCategory;
    });
  };

  // 调试当前脚本
  const handleDebug = async () => {
    const currentScript = scripts[currentScriptType];
    if (!currentScript || currentScript.trim() === '') {
      message.warning('当前脚本为空，无法调试');
      return;
    }

    try {
      setDebugLoading(true);
      setDebugResult(null);
      setExecutionLogs([]);

      const formScripts = getFormScripts ? getFormScripts() : {};

      // 构建请求
      const request = {
        scenarioId: scenario?.id,
        businessScenarioId: scenario?.businessScenarioId,
        scriptType: currentScriptType,
        preScript: scripts.PRE_SCRIPT || formScripts.preScript || '',
        assertionScript: scripts.ASSERTION_SCRIPT || formScripts.assertionScript || '',
        metricStatsScript: scripts.METRIC_STATS_SCRIPT || formScripts.metricStatsScript || '',
        metricConfig: formScripts.metricConfig || '',
        randomSelectUseCase: true,
        serviceConfig: serviceConfig,
      };

      // 调用同步接口
      const result = await judgeScenarioApi.aiDebugScript(request);
      
      setDebugResult(result);
      setExecutionLogs(result.executionLogs || []);

      if (result.passed) {
        message.success('调试通过！');
      } else {
        message.warning('调试未通过，请检查脚本');
      }
    } catch (error) {
      message.error('调试失败: ' + (error.message || '未知错误'));
      setDebugResult({
        passed: false,
        error: error.message || '调试失败'
      });
    } finally {
      setDebugLoading(false);
    }
  };

  // 根据规则描述重新生成脚本
  const handleRegenerate = async () => {
    if (!debugRuleDesc || debugRuleDesc.trim() === '') {
      message.warning('请先填写修复建议或规则描述');
      return;
    }

    try {
      setRegenerateLoading(true);

      const request = {
        scenarioId: scenario?.id,
        businessScenarioId: scenario?.businessScenarioId,
        scriptType: currentScriptType,
        assertionRuleDesc: debugRuleDesc,
        errorLog: debugResult?.error
          ? (typeof debugResult.error === 'string' ? debugResult.error : JSON.stringify(debugResult.error))
          : '',
        originalScript: scripts[currentScriptType] || '',
        serviceConfig: serviceConfig,
        scenarioCategory: scenarioCategory,
      };

      const asyncResult = await judgeScenarioApi.aiRegenerateScriptAsync(request);
      const taskId = asyncResult.taskId;

      const maxPollCount = 60;
      const pollInterval = 2000;

      for (let i = 0; i < maxPollCount; i++) {
        const statusResult = await judgeScenarioApi.getAsyncTaskStatus(taskId);

        if (statusResult.status === 'SUCCESS') {
          const result = statusResult.result;
          
          // 更新当前脚本
          if (result.generatedScript) {
            setScripts(prev => ({
              ...prev,
              [currentScriptType]: result.generatedScript
            }));
            message.success('脚本已重新生成');
          }
          
          // 清空之前的调试结果，准备重新调试
          setDebugResult(null);
          setExecutionLogs([]);
          setDebugRuleDesc('');
          break;
        } else if (statusResult.status === 'FAILED') {
          throw new Error(statusResult.error || '重新生成失败');
        } else if (statusResult.status === 'NOT_FOUND') {
          throw new Error('任务不存在或已过期');
        }

        await new Promise(resolve => setTimeout(resolve, pollInterval));
      }
    } catch (error) {
      message.error('重新生成失败: ' + (error.message || '未知错误'));
    } finally {
      setRegenerateLoading(false);
    }
  };

  // 应用脚本到表单
  const handleApply = () => {
    if (onApply) {
      const applyData = {};
      if (currentScriptType === 'PRE_SCRIPT' && scripts.PRE_SCRIPT) {
        applyData.preScript = scripts.PRE_SCRIPT;
      }
      if (currentScriptType === 'ASSERTION_SCRIPT' && scripts.ASSERTION_SCRIPT) {
        applyData.assertionScript = scripts.ASSERTION_SCRIPT;
      }
      if (currentScriptType === 'METRIC_STATS_SCRIPT' && scripts.METRIC_STATS_SCRIPT) {
        applyData.metricStatsScript = scripts.METRIC_STATS_SCRIPT;
      }
      onApply(applyData);
      message.success(`${getCurrentTypeConfig().label}已应用到表单`);
      onCancel();
    }
  };

  // 复制脚本到剪贴板
  const handleCopyScript = () => {
    const script = scripts[currentScriptType];
    if (script) {
      navigator.clipboard.writeText(script);
      message.success('已复制到剪贴板');
    }
  };

  // 自动填充错误信息
  const handleAutoFillError = () => {
    if (debugResult?.error) {
      const errorStr = typeof debugResult.error === 'string'
        ? debugResult.error
        : JSON.stringify(debugResult.error);
      setDebugRuleDesc(`请修复以下错误：${errorStr}`);
      message.info('已将错误信息填充到修复建议区域');
    }
  };

  return (
    <>
    <Modal
      title={
        <span>
          <BugOutlined style={{ marginRight: 8 }} />
          调试脚本
        </span>
      }
      open={visible}
      onCancel={onCancel}
      width={1000}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          关闭
        </Button>,
        <Button
          key="apply"
          icon={<CheckCircleOutlined />}
          onClick={handleApply}
          disabled={!scripts[currentScriptType]}
        >
          应用到表单
        </Button>,
        // AI重新生成按钮 - 仅在调试完成后显示
        debugResult && (
          <Button
            key="regenerate"
            type="primary"
            icon={<RobotOutlined />}
            onClick={() => {
              // 先滚动到顶部让用户填写描述
              window.scrollTo({ top: 0, behavior: 'smooth' });
              // 聚焦到描述输入框
              setTimeout(() => {
                const textarea = document.querySelector('textarea[placeholder*="描述"]');
                if (textarea) textarea.focus();
              }, 300);
              // 调用重新生成函数
              handleRegenerate();
            }}
            disabled={!debugRuleDesc?.trim()}
            loading={regenerateLoading}
          >
            AI重新生成{getCurrentTypeConfig().label}
          </Button>
        )
      ].filter(Boolean)}
    >
      <div className="space-y-4">
        {/* 测试数据缺失提示 */}
        {scenarioCategory === 'AUTO_REVIEW' && scenario?.testDataReady === false && (
          <Alert
            message="测试数据尚未就绪，断言脚本和指标统计脚本调试可能无法正常进行。请保存评测场景并等待测试数据生成，或手动刷新测试数据。"
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
          />
        )}
        {/* 脚本类型选择 */}
        <Card size="small">
          <div style={{ marginBottom: 12 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>选择脚本类型</Text>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
              {getAvailableScriptTypes().map(item => {
                const isSelected = currentScriptType === item.value;
                return (
                  <div
                    key={item.value}
                    onClick={() => {
                      setCurrentScriptType(item.value);
                      setDebugResult(null);
                      setExecutionLogs([]);
                      setDebugRuleDesc('');
                    }}
                    style={{
                      padding: '10px 14px',
                      border: `2px solid ${isSelected ? '#1677ff' : '#e8e8e8'}`,
                      borderRadius: 8,
                      cursor: 'pointer',
                      background: isSelected ? '#e6f4ff' : '#fff',
                      transition: 'all 0.2s',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 16, color: isSelected ? '#1677ff' : '#666' }}>
                        {item.icon}
                      </span>
                      <Text strong style={{ fontSize: 13, color: isSelected ? '#1677ff' : '#262626' }}>
                        {item.label}
                      </Text>
                      {scripts[item.value] && scripts[item.value].trim() && (
                        <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 12, marginLeft: 'auto' }} />
                      )}
                    </div>
                    <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.4 }}>
                      {item.desc}
                    </Text>
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
            <Button
              icon={<CopyOutlined />}
              onClick={handleCopyScript}
              disabled={!scripts[currentScriptType]}
            >
              复制脚本
            </Button>
            <Button
              type="primary"
              icon={<BugOutlined />}
              onClick={handleDebug}
              loading={debugLoading}
              disabled={!scripts[currentScriptType]}
            >
              执行调试
            </Button>
          </div>
        </Card>

        {/* 脚本编辑区 */}
        <Card size="small" title={getCurrentTypeConfig().label}>
          <CodeEditor
            key={currentScriptType}
            value={scripts[currentScriptType] || ''}
            onChange={(val) => setScripts(prev => ({ ...prev, [currentScriptType]: val }))}
            defaultHeight={200}
          />
        </Card>

        {/* 调试结果 */}
        {(debugResult || executionLogs.length > 0) && (
          <Card size="small" title="调试结果">
            {/* 用例信息 */}
            {debugResult?.useCaseName && (
              <div className="mb-4">
                <div className="font-medium mb-2">调试用例：</div>
                <div className="bg-blue-50 p-3 rounded text-sm border border-blue-200">
                  <div className="mb-1">
                    <span className="text-gray-600">用例名称：</span>
                    <span className="font-medium">{debugResult.useCaseName}</span>
                  </div>
                  {debugResult.useCaseId && (
                    <div className="mb-1">
                      <span className="text-gray-600">用例ID：</span>
                      <span className="font-mono">{debugResult.useCaseId}</span>
                    </div>
                  )}
                  {debugResult.expectedAnswer && (
                    <div>
                      <span className="text-gray-600">期望答案：</span>
                      <pre className="mt-1 bg-white p-2 rounded text-xs overflow-auto max-h-40 border border-blue-100" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {typeof debugResult.expectedAnswer === 'string' 
                          ? debugResult.expectedAnswer 
                          : JSON.stringify(debugResult.expectedAnswer, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            )}

            {debugResult && (
              <div className="mb-4">
                <Alert
                  message={debugResult.passed ? '调试通过' : '调试未通过'}
                  description={
                    debugResult.error
                      ? (typeof debugResult.error === 'string' ? debugResult.error : JSON.stringify(debugResult.error, null, 2))
                      : (debugResult.passed ? '脚本执行成功' : '请检查脚本逻辑')
                  }
                  type={debugResult.passed ? 'success' : 'error'}
                  showIcon
                  icon={debugResult.passed ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                />
              </div>
            )}

            {/* 执行日志 */}
            {executionLogs.length > 0 && (
              <div className="mb-4">
                <div className="font-medium mb-2">执行日志：</div>
                <div className="bg-gray-50 p-3 rounded text-sm font-mono max-h-40 overflow-auto">
                  {executionLogs.map((log, idx) => (
                    <div key={idx} className="text-gray-700">{log}</div>
                  ))}
                </div>
              </div>
            )}

            {/* 脚本输出值 */}
            {debugResult && !debugResult.error && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">脚本输出值：</span>
                  <Space>
                    {/* 机审结果页预览按钮 - 仅在指标统计脚本时显示 */}
                    {currentScriptType === 'METRIC_STATS_SCRIPT' && debugResult.metricStatsScriptOutput && (
                      <Button
                        size="small"
                        type="primary"
                        icon={<BarChartOutlined />}
                        onClick={() => {
                          const outputData = debugResult.metricStatsScriptOutput;
                          if (outputData) {
                            try {
                              const parsedData = typeof outputData === 'string' ? JSON.parse(outputData) : outputData;
                              setMetricOutputPreviewData(parsedData);
                              setShowMetricOutputPreview(true);
                            } catch (e) {
                              message.error('输出数据格式错误，无法预览');
                            }
                          }
                        }}
                      >
                        机审结果页
                      </Button>
                    )}
                    <Button
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => {
                      let outputValue = '';
                      if (currentScriptType === 'PRE_SCRIPT') {
                        outputValue = debugResult.preScriptOutput;
                      } else if (currentScriptType === 'ASSERTION_SCRIPT') {
                        outputValue = debugResult.assertionOutput;
                      } else if (currentScriptType === 'METRIC_STATS_SCRIPT') {
                        outputValue = debugResult.metricStatsScriptOutput;
                      }
                      
                      const outputText = typeof outputValue === 'string' 
                        ? outputValue 
                        : JSON.stringify(outputValue, null, 2);
                      
                      navigator.clipboard.writeText(outputText).then(() => {
                        message.success('输出值已复制到剪贴板');
                      }).catch(() => {
                        message.error('复制失败，请手动复制');
                      });
                    }}
                    disabled={
                      (currentScriptType === 'PRE_SCRIPT' && !debugResult.preScriptOutput) ||
                      (currentScriptType === 'ASSERTION_SCRIPT' && !debugResult.assertionOutput) ||
                      (currentScriptType === 'METRIC_STATS_SCRIPT' && !debugResult.metricStatsScriptOutput)
                    }
                  >
                    复制
                  </Button>
                  </Space>
                </div>
                <div className="bg-blue-50 p-3 rounded text-sm font-mono max-h-60 overflow-auto border border-blue-200">
                  {currentScriptType === 'PRE_SCRIPT' && debugResult.preScriptOutput && (
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                      {typeof debugResult.preScriptOutput === 'string' 
                        ? debugResult.preScriptOutput 
                        : JSON.stringify(debugResult.preScriptOutput, null, 2)}
                    </pre>
                  )}
                  {currentScriptType === 'PRE_SCRIPT' && debugResult.serviceResponse && (
                    <div style={{ marginTop: 12, borderTop: '1px dashed #91d5ff', paddingTop: 12 }}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-xs">
                          服务响应：
                          {debugResult.serviceCallSuccess === true && <Tag color="success" style={{ marginLeft: 4 }}>成功</Tag>}
                          {debugResult.serviceCallSuccess === false && <Tag color="error" style={{ marginLeft: 4 }}>失败</Tag>}
                        </span>
                        <Button
                          size="small"
                          icon={<CopyOutlined />}
                          onClick={() => {
                            const text = typeof debugResult.serviceResponse === 'string'
                              ? debugResult.serviceResponse
                              : JSON.stringify(debugResult.serviceResponse, null, 2);
                            navigator.clipboard.writeText(text).then(() => {
                              message.success('服务响应已复制到剪贴板');
                            }).catch(() => {
                              message.error('复制失败');
                            });
                          }}
                        >
                          复制
                        </Button>
                      </div>
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                        {typeof debugResult.serviceResponse === 'string'
                          ? debugResult.serviceResponse
                          : JSON.stringify(debugResult.serviceResponse, null, 2)}
                      </pre>
                    </div>
                  )}
                  {currentScriptType === 'ASSERTION_SCRIPT' && debugResult.assertionOutput && (
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                      {typeof debugResult.assertionOutput === 'string' 
                        ? debugResult.assertionOutput 
                        : JSON.stringify(debugResult.assertionOutput, null, 2)}
                    </pre>
                  )}
                  {currentScriptType === 'METRIC_STATS_SCRIPT' && debugResult.metricStatsScriptOutput && (
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                      {typeof debugResult.metricStatsScriptOutput === 'string' 
                        ? debugResult.metricStatsScriptOutput 
                        : JSON.stringify(debugResult.metricStatsScriptOutput, null, 2)}
                    </pre>
                  )}
                  {((currentScriptType === 'PRE_SCRIPT' && !debugResult.preScriptOutput) ||
                    (currentScriptType === 'ASSERTION_SCRIPT' && !debugResult.assertionOutput) ||
                    (currentScriptType === 'METRIC_STATS_SCRIPT' && !debugResult.metricStatsScriptOutput)) && (
                    <div className="text-gray-500">无输出值</div>
                  )}
                </div>
              </div>
            )}

            {/* 错误信息 */}
            {debugResult?.error && (
              <div className="mb-4">
                <div className="font-medium mb-2">错误信息：</div>
                <div className="bg-red-50 p-3 rounded text-sm font-mono max-h-40 overflow-auto border border-red-200 text-red-700">
                  {typeof debugResult.error === 'string' 
                    ? debugResult.error 
                    : JSON.stringify(debugResult.error, null, 2)}
                </div>
              </div>
            )}

            {/* AI重新生成描述输入框 - 无论调试是否通过都显示 */}
            {debugResult && (
              <div className="mt-4">
                <Divider>AI重新生成脚本</Divider>
                <div className="mb-2 text-sm text-gray-600">
                  当前脚本类型：<Tag color="blue">{getCurrentTypeConfig().label}</Tag>
                  {!debugResult.error ? (
                    <span className="ml-2 text-green-600">✓ 调试通过，但如果不符合预期可重新生成</span>
                  ) : (
                    <span className="ml-2 text-red-600">✗ 调试失败，请描述修复建议</span>
                  )}
                </div>
                <TextArea
                  value={debugRuleDesc}
                  onChange={(e) => setDebugRuleDesc(e.target.value)}
                  placeholder={debugResult.error 
                    ? "描述修复建议或规则要求，AI将重新生成脚本..." 
                    : "描述您期望的脚本逻辑，例如：请修改断言逻辑，增加对ruleDetails的校验..."
                  }
                  rows={3}
                  className="mb-2"
                />
                {debugResult.error && (
                  <Button
                    size="small"
                    onClick={handleAutoFillError}
                    disabled={!debugResult?.error}
                    style={{ marginBottom: 8 }}
                  >
                    自动填充错误
                  </Button>
                )}
              </div>
            )}
          </Card>
        )}
      </div>
    </Modal>

    {/* 指标统计脚本输出预览弹窗 - 全屏显示 */}
    <Modal
      title={
        <Space>
          <BarChartOutlined style={{ color: '#1890ff', fontSize: '20px' }} />
          <span>机审结果统计页预览</span>
          <Tag color="blue">调试输出</Tag>
        </Space>
      }
      open={showMetricOutputPreview}
      onCancel={() => {
        setShowMetricOutputPreview(false);
        setMetricOutputPreviewData(null);
      }}
      footer={[
        <Button 
          key="close" 
          type="primary"
          onClick={() => {
            setShowMetricOutputPreview(false);
            setMetricOutputPreviewData(null);
          }}
        >
          关闭
        </Button>
      ]}
      width="95%"
      centered
      style={{ top: 10, paddingBottom: 10 }}
      bodyStyle={{ 
        height: 'calc(100vh - 120px)', 
        overflow: 'auto', 
        padding: '24px'
      }}
      destroyOnClose
    >
      {metricOutputPreviewData ? (
        <AutoReviewStatsDisplay 
          data={metricOutputPreviewData} 
        />
      ) : (
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
          暂无数据
        </div>
      )}
    </Modal>
    </>
  );
}

export default ScriptDebugModal;
